/**
 * Theme Context Provider
 * 
 * Manages the application's dark/light theme state with:
 * - Automatic detection of system preference
 * - Persistent storage in localStorage
 * - Synchronization with Tailwind CSS dark mode
 * - Theme toggling functionality
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeContextType } from '../types';

// Create theme context with undefined default
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * Custom hook to use theme context
 * Throws error if used outside of ThemeProvider to prevent runtime issues
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

/**
 * Theme Provider Component
 * Wraps the application to provide theme context to all children
 */
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [darkMode, setDarkMode] = useState(true);

  /**
   * Initialize theme from localStorage or system preference on component mount
   */
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    
    if (savedTheme) {
      // Use saved theme preference
      setDarkMode(savedTheme === 'dark');
      return;
    }
    
    // Fall back to system preference
    const systemPrefersDark = window.matchMedia && 
      window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDarkMode(systemPrefersDark);
  }, []);

  /**
   * Synchronize theme changes with DOM for Tailwind CSS dark mode
   */
  useEffect(() => {
    const htmlElement = document.documentElement;
    
    if (darkMode) {
      htmlElement.classList.add('dark');
      htmlElement.setAttribute('data-theme', 'dark');
    } else {
      htmlElement.classList.remove('dark');
      htmlElement.setAttribute('data-theme', 'light');
    }
  }, [darkMode]);

  /**
   * Toggle theme and persist preference to localStorage
   */
  const toggleDarkMode = () => {
    const newMode = !darkMode;
    setDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </ThemeContext.Provider>
  );
};